// Step definitions
export interface BuildAgentStep {
  id: number;
  key: string;
  title: string;
  icon: string;
}

// Communication styles
export type CommunicationStyle =
  | "friendly_professional"
  | "formal"
  | "casual"
  | "consultative";

// New level mapping structure matching the API format
export interface LevelMappingCondition {
  value: string;
  selected: boolean;
  deletable: boolean;
}

export interface LevelMapping {
  hot: LevelMappingCondition[];
  warm: LevelMappingCondition[];
  cold: LevelMappingCondition[];
  unqualified: LevelMappingCondition[];
}

// Legacy level mapping structure for backward compatibility
export interface LevelMappingItem {
  text: string;
  isSelected: boolean;
}

export interface ApiLevelMapping {
  cold: LevelMappingItem[];
  warm: LevelMappingItem[];
  hot: LevelMappingItem[];
  unqualified: LevelMappingItem[];
}

// UI level mapping structure (flattened for easier manipulation)
export interface UiLevelMappingItem extends LevelMappingItem {
  leadType: LeadType; // Added to track which lead type this item belongs to
}

// Qualification criteria
export interface QualificationCriteria {
  name: string;
  question: string | string[];
  weightage: number;
  enabled: boolean;
  type?: "budget" | "authority" | "need" | "timeline" | "custom";
  level_mapping: LevelMapping; // Updated to use new format
  is_default: boolean; // If true, this item cannot be deleted or have its name edited
}

// Lead action configuration
export interface LeadActionConfig {
  leadType: LeadType;
  actions: ActionItem[];
}

// Lead types
export type LeadType = "hot" | "warm" | "cold" | "unqualified";

// Action items
export interface ActionItem {
  id: string;
  type: ActionType;
  name: string;
  config: Record<string, any>;
  enabled: boolean;
}

// Action types
export enum ActionType {
  hubspot = "hubspot",
  salesforce = "salesforce",
  monday = "monday",
  zoho = "zoho",
  slack = "slack",
  calendly = "calendly"
}

// Component Props interface
export interface StepComponentProps {
  data: ApiBuildAgentConfig;
  onChange: (updates: Partial<ApiBuildAgentConfig>) => void;
}

// Validation rules
export interface ValidationRules {
  [key: number]: (data: ApiBuildAgentConfig) => boolean;
}

export enum FormFieldType {
  Text = "text",
  Email = "email",
  Phone = "phone",
  Dropdown = "dropdown",
  File = "file_attachment",
  Checkbox = "checkbox",
  SubmitMessage = "submit_message"
}

interface BaseFieldProps {
  id: string;
  label: string;
  placeholder: string;
  required: boolean;
}

export interface FieldTextProps extends BaseFieldProps {
  type: FormFieldType.Text | FormFieldType.Email | FormFieldType.Phone;
}

export interface FieldDropdownProps extends BaseFieldProps {
  type: FormFieldType.Dropdown;
  config: {
    options: {
      value: string;
      label: string;
      selected: boolean;
    }[];
    allow_multiple: boolean;
  };
}

export interface FieldFileProps extends BaseFieldProps {
  type: FormFieldType.File;
  config: {
    allow_multiple_files: boolean;
    allowed_file_types: string[];
    max_file_size: number; // 10485760, // 10MB
  };
}

export interface FieldCheckboxProps extends BaseFieldProps {
  type: FormFieldType.Checkbox;
  config: {
    checkbox_text: string;
  };
}

export interface FieldSubmitMessageProps extends BaseFieldProps {
  type: FormFieldType.SubmitMessage;
  config: {
    message: string;
  };
}

export type TagItemProps =
  | FieldTextProps
  | FieldDropdownProps
  | FieldFileProps
  | FieldCheckboxProps
  | FieldSubmitMessageProps;

export interface PrimaryGoalItem {
  value: string;
  selected: boolean;
  deletable: boolean;
}

export interface LeadContactFields {
  fields: TagItemProps[];
  use_form: boolean;
  version: string;
  closable: boolean;
}

// API Configuration interfaces matching the new API structure
export interface ApiBuildAgentConfig {
  instructions: string;
  communication_style: string;
  primary_goals: PrimaryGoalItem[];
  multi_lingual: boolean;
  /**
   * @deprecated onboarding_step field is being deprecated in favor of completed_onboarding_steps.
   * This field was used for linear step progression but is no longer needed as users can now
   * jump to any tab to complete operations.
   */
  onboarding_step: number;
  completed_onboarding_steps: number[];
  form_display_config: {
    maximum_attempts: number;
    strategy: number;
    trigger_frequency: number;
  };
  lead_contact_fields: LeadContactFields;
  qualification_config: {
    qualification_criteria: Array<{
      name: string;
      question: string | string[];
      enabled: boolean;
      level_mapping: LevelMapping; // Updated to use the new format
      weightage: number;
      is_default: boolean;
    }>;
  };
  actions: {
    action_mapping: {
      hot: Array<{ id: string; name: string }>;
      warm: Array<{ id: string; name: string }>;
      cold: Array<{ id: string; name: string }>;
      unqualified: Array<{ id: string; name: string }>;
    };
  };
}

// AI Actions specific types
// Meeting Action interface based on the API response structure
export interface MeetingAction {
  action_id: string;
  message: string;
  ui_type: string;
  button_text: string;
  cta_url: string;
  action_type?: string;
  name?: string;
}

// Dialog state interfaces
export interface IntegrationCardState {
  card: any | null;
  index: number;
  open: boolean;
}

export interface ActionConnectDialogState {
  action: {
    id: string;
    name: string;
    integration: ActionType;
  } | null;
  leadType: LeadType | null;
  open: boolean;
}

export interface ConfigDrawerState {
  action: ActionItem | null;
  open: boolean;
  isFromDropdown?: boolean;
  leadType?: LeadType | null;
  isConnected?: boolean;
}

export interface DeleteConfirmDialogState {
  open: boolean;
  actionId: string | null;
  leadType: LeadType | null;
  actionType: ActionType | null;
  actionName: string;
}

export interface UpdateAttributeDialogState {
  open: boolean;
  action: ActionItem | null;
  leadType: LeadType | null;
}

export interface IntegrationDialogActionItem {
  id: string;
  name: string;
  integration: ActionType;
}
